import { createApi } from "@reduxjs/toolkit/query/react";
import { baseApiQuery } from "../../../shared/api";
import {
  BaseResponse,
  PermissionUsersResponse,
  PermissionsResponse,
  SearchUsersParams,
  SearchUsersResponse,
  UsersWithAccessResponse,
} from "./types";

export const adminAccessApi = createApi({
  reducerPath: "adminAccessApi",
  baseQuery: baseApiQuery,
  tagTypes: ["Permissions", "PermissionUsers", "UsersWithAccess"],
  endpoints: (builder) => ({
    // Get all permissions
    getPermissions: builder.query<PermissionsResponse, void>({
      query: () => "/admin/access/permissions",
      providesTags: ["Permissions"],
    }),

    // Get users with specific permission
    getPermissionUsers: builder.query<PermissionUsersResponse, number>({
      query: (id) => `/admin/access/permissions/${id}/users`,
      providesTags: (_result, _error, id) => [{ type: "PermissionUsers", id }],
    }),

    // Add user to permission
    addUserToPermission: builder.mutation<
      BaseResponse,
      { id: number; userId: string }
    >({
      query: ({ id, userId }) => ({
        url: `/admin/access/permissions/${id}/users/${userId}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "PermissionUsers", id },
        "UsersWithAccess",
      ],
    }),

    // Remove user from permission
    removeUserFromPermission: builder.mutation<
      BaseResponse,
      { id: number; userId: string }
    >({
      query: ({ id, userId }) => ({
        url: `/admin/access/permissions/${id}/users/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "PermissionUsers", id },
        "UsersWithAccess",
      ],
    }),

    // Get all users with their permissions and groups
    getUsersWithAccess: builder.query<UsersWithAccessResponse, void>({
      query: () => "/admin/access/users",
      providesTags: ["UsersWithAccess"],
    }),

    // Search users
    searchUsers: builder.query<SearchUsersResponse, SearchUsersParams>({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params.phoneNumber)
          queryParams.append("phoneNumber", params.phoneNumber);
        if (params.firstName) queryParams.append("firstName", params.firstName);
        if (params.lastName) queryParams.append("lastName", params.lastName);

        return `/admin/access/users/search${
          queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`;
      },
    }),
  }),
});

export const {
  useGetPermissionsQuery,
  useGetPermissionUsersQuery,
  useAddUserToPermissionMutation,
  useRemoveUserFromPermissionMutation,
  useGetUsersWithAccessQuery,
  useLazySearchUsersQuery,
} = adminAccessApi;
